import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  SetLoyaltyCardRewardsTierMutationHookResult,
  useSetLoyaltyCardRewardsTierMutation,
} from 'generated/rbi-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { useBankMyRewardsTierId, useSelectedRewardTier } from 'hooks/loyalty/use-selected-reward';
import useErrorModal from 'hooks/use-error-modal';
import { useToggleState } from 'hooks/use-toggle-state';
import { RewardTierOptionContainer } from 'pages/account/tims-rewards/reward-categories';
import { BankMyRewardsLabel } from 'pages/account/tims-rewards/reward-categories/components/bank-my-rewards-label';
import { CategoryDropdown } from 'pages/account/tims-rewards/reward-categories/components/category-dropdown';
import RewardCategoryDropdownHeader from 'pages/account/tims-rewards/reward-categories/components/category-dropdown-header';
import { CategoryTitle } from 'pages/account/tims-rewards/reward-categories/components/category-title';
import {
  RewardTierInput,
  RewardTierNoInput,
} from 'pages/account/tims-rewards/reward-categories/components/reward-tier';
import { RewardCategoriesModal } from 'pages/account/tims-rewards/reward-categories/reward-categories-modal';
import { WithStyleVariant } from 'pages/account/tims-rewards/reward-categories/types';
import { useAuthContext } from 'state/auth';
import { useThLoyaltyContext } from 'state/loyalty';
import { ILoyaltyCategory } from 'state/loyalty/types';
// TODO: remove min-height: 158px; in favor of long term fix
// min-height is a workaround for the safari nested flexbox issue
// https://github.com/philipwalton/flexbugs/issues/271
// causing wrapper to not wrap the dropdown elements properly
const CartRewardCategoryDropdownWrapper = styled.div`
  min-height: 158px;
  z-index: ${Styles.zIndex.normal};
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  align-items: center;
  flex-wrap: nowrap;
  padding: 1.3rem 1rem 1.7rem 1rem;
  background: url(./assets/th/cart-dropdown-bg.svg) no-repeat;
  background-size: cover;
`;

type LoyaltyCategoryOption = ILoyaltyCategory['options'][number];

type SetSelectedRewardsTierFunction = SetLoyaltyCardRewardsTierMutationHookResult[0];

export const RewardsDropdown: React.FC<
  {
    radioButtonName: string;
    setSelectedRewardsTier: SetSelectedRewardsTierFunction;
  } & WithStyleVariant
> = ({ radioButtonName, variant, setSelectedRewardsTier }) => {
  const { formatMessage } = useIntl();
  const { loyaltyDetails } = useLoyaltyActiveCard();
  const { getLoyaltyCategory } = useThLoyaltyContext();
  const { user } = useAuthContext();

  const loyaltyTier = (user && user.details && user.details.loyaltyTier) || '';
  const [dropdownOptionsVisible, toggleDropdownOptionsVisible] = useToggleState();

  const [showRewardCategoriesModal, setShowRewardCategoriesModal] = useState(false);
  const [loyaltyCategories, setLoyaltyCategories] = useState<ILoyaltyCategory[]>([]);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Setting Reward Tier Failure',
  });
  const onSetRewardsTierError = useCallback(
    () => openErrorDialog({ message: formatMessage({ id: 'setRewardsTierError' }) }),
    [openErrorDialog, formatMessage]
  );

  useEffect(() => {
    getLoyaltyCategory().then((availableLoyaltyCategories: Array<ILoyaltyCategory> | null) =>
      setLoyaltyCategories(availableLoyaltyCategories || [])
    );
  }, [getLoyaltyCategory, setLoyaltyCategories]);

  const { bankMyRewardTierId } = useBankMyRewardsTierId();
  const { selectedRewardTierId, onChangeRewardTier } = useSelectedRewardTier(
    loyaltyCategories,
    setSelectedRewardsTier,
    { onSetRewardsTierError }
  );

  const selectedTier = useMemo(
    () =>
      selectedRewardTierId
        ? loyaltyCategories.reduce((acc: { [key: string]: LoyaltyCategoryOption }, { options }) => {
            options.forEach(val => (acc[val._id] = val));
            return acc;
          }, {})[selectedRewardTierId]
        : undefined,
    [selectedRewardTierId, loyaltyCategories]
  );

  const title = useMemo(() => {
    let dropdownTitle = (
      <CategoryTitle variant={variant}>{formatMessage({ id: 'pleaseSelect' })}</CategoryTitle>
    );

    const dropdownOptionsNotVisible = !dropdownOptionsVisible;
    const shouldShowBankMyRewardsTitle =
      dropdownOptionsNotVisible && loyaltyTier === bankMyRewardTierId;

    if (shouldShowBankMyRewardsTitle) {
      dropdownTitle = (
        <RewardTierNoInput
          name={formatMessage({ id: 'bankMyRewards' })}
          tierLabel={<BankMyRewardsLabel variant={variant} />}
          variant={variant}
        />
      );
    }

    if (dropdownOptionsNotVisible && selectedTier !== undefined) {
      dropdownTitle = (
        <RewardTierNoInput
          name={selectedTier.name?.locale}
          tierLabel={selectedTier.points}
          variant={variant}
          points={selectedTier.points}
        />
      );
    }

    return dropdownTitle;
  }, [
    bankMyRewardTierId,
    dropdownOptionsVisible,
    formatMessage,
    loyaltyTier,
    selectedTier,
    variant,
  ]);

  return (
    <>
      {showRewardCategoriesModal && (
        <RewardCategoriesModal
          onDismiss={() => setShowRewardCategoriesModal(false)}
          radioButtonName="see-more-options-modal-reward-tier"
          setSelectedRewardsTier={setSelectedRewardsTier}
        />
      )}
      <RewardCategoryDropdownHeader
        variant={variant}
        points={loyaltyDetails ? loyaltyDetails.points : 0}
      />
      <CategoryDropdown
        id="rewards-dropdown"
        label={formatMessage({ id: 'selectedRewardLevelAndBankMyRewards' })}
        onClickSeeMoreActions={() => setShowRewardCategoriesModal(true)}
        optionsVisible={dropdownOptionsVisible}
        overlay
        title={title}
        toggleOptionsVisible={toggleDropdownOptionsVisible}
        variant={variant}
      >
        <RewardTierOptionContainer
          onClick={() => onChangeRewardTier(bankMyRewardTierId)}
          variant={variant}
        >
          <RewardTierInput
            name={formatMessage({ id: 'bankMyRewards' })}
            radioButtonName={radioButtonName}
            selected={selectedRewardTierId === bankMyRewardTierId}
            standalone={false}
            tierLabel={<BankMyRewardsLabel variant={variant} />}
            value={bankMyRewardTierId || ''}
            variant={variant}
          />
        </RewardTierOptionContainer>
        {selectedTier && (
          <RewardTierOptionContainer
            onClick={() => onChangeRewardTier(bankMyRewardTierId)}
            variant={variant}
          >
            <RewardTierInput
              name={selectedTier.name?.locale}
              radioButtonName={radioButtonName}
              selected={selectedRewardTierId === selectedTier._id}
              tierLabel={selectedTier.points}
              value={selectedTier._id}
              variant={variant}
            />
          </RewardTierOptionContainer>
        )}
        <ErrorDialog />
      </CategoryDropdown>
    </>
  );
};

export const CartRewardDropdown = () => {
  const [setLoyaltyCardRewardsTier] = useSetLoyaltyCardRewardsTierMutation();
  return (
    <CartRewardCategoryDropdownWrapper>
      <RewardsDropdown
        radioButtonName="cart-rewards-dropdown-reward-tier"
        setSelectedRewardsTier={setLoyaltyCardRewardsTier}
      />
    </CartRewardCategoryDropdownWrapper>
  );
};
